
import React, { useState, useEffect } from 'react';

import { DashboardTab, generarDocsFake } from '../Informes/InformesPage';

import {
  FileStack,
  RefreshCcw,
  AlertCircle,
  UserCheck,
  Users,
  ShieldCheck,
  BarChart2,
  Plug2,
  Server,
  Info,
  FileText,
  FileCheck,
  FileMinus,
  Layers3,
  Gem,
} from 'lucide-react';

// Simulación de resoluciones para el resumen
const resolucionesDemo = [
  { numero: '18764000013', tipo: 'Factura electrónica', vence: '2024-04-15' },
  { numero: '18764000014', tipo: 'Doc. Soporte', vence: new Date(Date.now() + 8 * 86400000).toISOString().split('T')[0] },
  { numero: '18764000015', tipo: 'Factura electrónica', vence: '2026-01-01' },
];

// Obtener el mes actual en español (y la mayúscula inicial)
function mesActual() {
  const meses = [
    "enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"
  ];
  const fecha = new Date();
  const nombreMes = meses[fecha.getMonth()];
  return nombreMes.charAt(0).toUpperCase() + nombreMes.slice(1);
}

// Mensaje automático para tooltips
function tooltipKPI(label, mes) {
  if (label.toLowerCase().includes("factura")) {
    return `Tus facturas emitidas de ${mes}`;
  } else if (label.toLowerCase().includes("nota")) {
    return `Tus notas crédito/débito de ${mes}`;
  } else if (label.toLowerCase().includes("soporte")) {
    return `Tus documentos soporte de ${mes}`;
  } else if (label.toLowerCase().includes("evento")) {
    return `Tus eventos asociados de ${mes}`;
  } else if (label.toLowerCase().includes("documentos enviados")) {
    return `Tus documentos enviados de ${mes}`;
  } else if (label.toLowerCase().includes("aceptados")) {
    return `Tus documentos aceptados de ${mes}`;
  } else if (label.toLowerCase().includes("rechazados")) {
    return `Tus documentos rechazados de ${mes}`;
  }
  return `Tus ${label.toLowerCase()} de ${mes}`;
}

function resumenResoluciones(data) {
  let activas = 0;
  let proximas = 0;
  data.forEach(r => {
    const hoy = new Date();
    const fechaV = new Date(r.vence);
    const diff = Math.ceil((fechaV - hoy) / (1000 * 60 * 60 * 24));
    if (diff < 0) return;
    if (diff < 10) proximas += 1;
    else activas += 1;
  });
  return { activas, proximas };
}

// --- IMPORTANTE ---
// Declaramos accesosInicio **DENTRO** de la función para tener acceso a setActivePage

export default function DashboardPage ({ setActivePage }) {
  const mes = mesActual();
  const resumen = resumenResoluciones(resolucionesDemo);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setDocs(generarDocsFake());
      setLoading(false);
    }, 900);
  }, []);

  

  // Gadgets (accesos rápidos)
  const accesosInicio = [
    {
      id: 'preferencias',
      label: 'Mis Datos',
      icon: <Gem className="h-5 w-5 md:h-6 md:w-6 text-gray-400" />,
      desc: 'Mi Cuenta',
      onClick: () => setActivePage('preferencias')
    },
    {
      id: 'usuarios',
      label: 'Usuarios',
      icon: <Users className="h-5 w-5 md:h-6 md:w-6 text-[#003865]" />,
      desc: 'Registro de Usuarios',
      onClick: () => setActivePage('usuarios')
    },
    {
      id: 'resoluciones',
      label: 'Resoluciones Y Firma',
      icon: <Server className="h-5 w-5 md:h-6 md:w-6 text-gray-600" />,
      desc: 'Configuracion',
      onClick: () => setActivePage('resoluciones')
    },
    
    {
      id: 'facturas',
      label: 'Facturas Electrónicas',
      icon: <FileStack className="h-5 w-5 md:h-6 md:w-6 text-indigo-600" />,
      desc: 'Consulta y seguimiento de facturas',
      onClick: () => setActivePage('facturas')
    },
    {
      id: 'docsoporte',
      label: 'Doc. Soporte',
      icon: <RefreshCcw className="h-5 w-5 md:h-6 md:w-6 text-blue-500" />,
      desc: 'Documentos soporte y eventos',
      onClick: () => setActivePage('documentos')
    },
    {
      id: 'informes',
      label: 'Informes',
      icon: <BarChart2 className="h-5 w-5 md:h-6 md:w-6 text-fuchsia-600" />,
      desc: 'Visualización de reportes',
      onClick: () => setActivePage('informes')
    },
    {
      id: 'Mi Plan',
      label: 'Mi Plan',
      icon: <Info className="h-5 w-5 md:h-6 md:w-6 text-gray-400" />,
      desc: 'Starter',
      onClick: () => setActivePage('acerca')
    },
  ];

  // Recomendaciones (última fila, una sola fila, incluye Resoluciones especial)
  function getRecomendaciones(resumen) {
    return [
      {
        tipo: "resoluciones",
        icon: <Server className="h-8 w-8 text-[#003865]" />,
        badge: resumen.activas + resumen.proximas,
        titulo: "Resoluciones",
        resumen,
        onClick: () => setActivePage('facturas'),
      },
      {
        tipo: "normal",
        icon: <ShieldCheck className="h-8 w-8 text-teal-600" />,
        titulo: "Actualiza tu firma digital",
        texto: "Tu certificado digital vence pronto, ¡renuévalo para evitar rechazos!",
      },
      {
        tipo: "normal",
        icon: <Info className="h-8 w-8 text-blue-500" />,
        titulo: "Tip de uso",
        texto: "Puedes descargar reportes desde la sección Informes.",
      },
      {
        tipo: "normal",
        icon: <AlertCircle className="h-8 w-8 text-red-500" />,
        titulo: "Atención",
        texto: "Tienes documentos rechazados, revisa y corrige para mantener tu operación.",
      },
    ]
  };

  const recomendaciones = getRecomendaciones(resumen);

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-tr from-gray-50 to-gray-100">
      <main className="flex-1 flex flex-col justify-between h-screen p-2 md:p-6">
        <div>
          {/* Gadgets Grid */}
          <div
            className="grid w-full"
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '10px',
              marginBottom: '12px',
            }}
          >
            {accesosInicio.map(({ id, label, icon, desc, onClick }) => (
              <div
                key={id}
                className="bg-white rounded-lg shadow flex flex-col items-center justify-center gap-1 cursor-pointer border border-gray-100 transition-all duration-150
                  hover:bg-[#e3f2fd] hover:shadow-md hover:scale-105 hover:border-[#003865]"
                style={{
                  minHeight: 85,
                  minWidth: 100,
                  padding: 8,
                  fontSize: 13,
                }}
                onClick={onClick}
              >
                <div className="mb-1">{icon}</div>
                <div className="font-medium text-gray-800 text-center">{label}</div>
                <div className="text-gray-500 text-[11px] text-center leading-tight line-clamp-2">{desc}</div>
              </div>
            ))}
          </div>
        </div>
        {/* KPIs Título y grid */}
       
        <div className="min-h-screen flex flex-col bg-gradient-to-tr from-gray-50 to-gray-100 p-5">
          <DashboardTab docs={docs} loading={loading} />
        </div>
        
              
      </main>
    </div>
  );
}
